import { AxiosAdapter, AxiosPromise, AxiosResponse } from 'axios';

import { OfflineCacheRequestConfig } from 'types';

import encryptedTimestampCache from './encryptedTimestampCache';

const CACHE_FOUND_CODE = 200;
const CACHE_NOT_FOUND_CODE = 404;

const getCacheUrl = (config: OfflineCacheRequestConfig) => {
  if (config.cacheWithReqBody && config.data) {
    const reqParams: Record<string, unknown> = JSON.parse(config.data);
    const urlParams = Object.keys(reqParams).map((key) => `${key}:${reqParams[key]}`);
    urlParams.sort((a, b) => a.localeCompare(b));
    return `${config.url}?${urlParams.join('&')}`;
  }
  return config.url!;
};

const createSuccessCacheResponse = (config: OfflineCacheRequestConfig, data: unknown, status: number) =>
  ({
    config,
    data,
    status,
    statusText: 'OK',
    fromCache: true,
    headers: {},
  }) as AxiosResponse;

const isOffline = () => !window.navigator.onLine;

/**
 * The cache service implementation, create a cached axios adapter from original.
 * Show offline error component, and handle not cached error, if setted the setCacheResponseExist callback value
 * @param originalAdapter The module based axios default adapter
 * @param setCacheResponseExist Set this value, if you want to use the not cached response error component
 */
const createOfflineCacheAdapter = (originalAdapter: AxiosAdapter, setCacheResponseExist?: (value: boolean) => void) => {
  const offlineAdapter = async (config: OfflineCacheRequestConfig) => {
    const data = await encryptedTimestampCache.getItem(getCacheUrl(config));
    if (setCacheResponseExist) {
      setCacheResponseExist(!!data);
    }
    return createSuccessCacheResponse(config, data || '', data ? CACHE_FOUND_CODE : CACHE_NOT_FOUND_CODE);
  };

  const onlineAdapter = async (config: OfflineCacheRequestConfig) => {
    return originalAdapter(config).then((response) => {
      if (config.method?.toLowerCase() === 'get' || config.cacheWithReqBody) {
        encryptedTimestampCache.setItem(getCacheUrl(config), response.data);
      }
      return response;
    });
  };

  return (config: OfflineCacheRequestConfig): AxiosPromise => {
    return isOffline() ? offlineAdapter(config) : onlineAdapter(config);
  };
};

export default createOfflineCacheAdapter;
